/*
 * AAIKO bag panel behaviour.
 *
 * Horizon stays the cart engine: every mutation is a POST to /cart/change.js
 * and the fresh markup comes back through the Section Rendering API. Nothing
 * about cart state is computed here.
 *
 * Opening hooks, in order of preference:
 *   1. any element with [data-aaiko-cart-open]        (the header bag trigger)
 *   2. the `aaiko:cart:open` event on document
 *   3. a click on any link to the cart page, intercepted progressively
 *      (without JS the link still navigates to /cart)
 *   4. document.querySelector('aaiko-cart-drawer').open()
 *
 * Refresh hooks:
 *   - Shopify's standard `shopify:cart:lines-update` event, which Horizon's
 *     add-to-cart button dispatches. `add` also opens the panel.
 *
 * "You may also like" is Shopify's recommendations engine, keyed on the most
 * recently added line. The engine only answers on /recommendations/products,
 * so the section renders skeletons and this file fetches the fragment
 * sections/aaiko-cart-recs.liquid against that route and lifts the tiles in.
 */
(function () {
  'use strict';

  var CART_EVENT = 'shopify:cart:lines-update';
  var FOCUSABLE = [
    'a[href]',
    'button:not([disabled])',
    'select:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  function isCartUrl(href) {
    if (!href) return false;
    try {
      var url = new URL(href, window.location.origin);
      if (url.origin !== window.location.origin) return false;
      return /(^|\/)cart\/?$/.test(url.pathname);
    } catch (error) {
      return false;
    }
  }

  class AaikoCartDrawerComponent extends HTMLElement {
    constructor() {
      super();
      this.busy = false;
      this.checkingOut = false;
      this.previouslyFocused = null;
      // Last engine answer, keyed on the seed product, so a quantity change
      // (same seed, fresh markup) gets its tiles back synchronously instead
      // of a second round of skeletons and a second request.
      this.recsCache = null;
      this.recsRequest = null;
      this.onKeydown = this.onKeydown.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onCartUpdate = this.onCartUpdate.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onOpenEvent = this.onOpenEvent.bind(this);
    }

    connectedCallback() {
      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('submit', this.onSubmit);
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener(CART_EVENT, this.onCartUpdate);
      document.addEventListener('aaiko:cart:open', this.onOpenEvent);
      if (!this.isOpen && this.panel) this.panel.setAttribute('inert', '');
      // Fetched at load, not at open: only pages with a line in the bag carry
      // a seed, and those are the visitors who will open the panel. By then
      // the tiles are already there.
      this.loadRecs();
    }

    disconnectedCallback() {
      // Never strand the page in a locked state if the element goes away.
      this.lockScroll(false);
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener(CART_EVENT, this.onCartUpdate);
      document.removeEventListener('aaiko:cart:open', this.onOpenEvent);
    }

    get sectionId() {
      return this.dataset.sectionId || 'aaiko-cart-drawer';
    }

    get panel() {
      return this.querySelector('[data-aaiko-cart-panel]');
    }

    get isOpen() {
      return this.hasAttribute('open');
    }

    /* ---------------------------------------------------------------- open */

    open() {
      if (this.isOpen) return;
      // Capture the real opener (the add-to-bag button, the header bag link)
      // BEFORE dismissing anything else, so close() returns focus to it.
      this.previouslyFocused = document.activeElement;
      this.dismissOtherOverlays();
      this.setAttribute('open', '');
      this.lockScroll(true);
      var panel = this.panel;
      if (panel) panel.removeAttribute('inert');
      // Focus the dialog itself, not the close button: the panel takes focus
      // (so Escape and the Tab cycle are live, and screen readers announce the
      // dialog) without painting a focus ring on a control nobody chose.
      // A forced reflow first, because a still-`visibility: hidden` element
      // cannot take focus.
      if (panel) {
        void panel.offsetHeight;
        panel.focus();
      }
    }

    close() {
      if (!this.isOpen) return;
      this.removeAttribute('open');
      this.lockScroll(false);
      var panel = this.panel;
      if (panel) panel.setAttribute('inert', '');
      var restore = this.previouslyFocused;
      this.previouslyFocused = null;
      if (restore && typeof restore.focus === 'function' && document.contains(restore)) {
        restore.focus();
      } else {
        var trigger = document.querySelector('[data-aaiko-cart-open]');
        if (trigger) trigger.focus();
      }
    }

    toggle() {
      if (this.isOpen) this.close();
      else this.open();
    }

    /**
     * Freezes the page behind the panel. One class on <html>, never an inline
     * style, so it cannot collide with the search panel's own save/restore of
     * `.page-wrapper`. Single toggle point, so every close path (Escape, scrim,
     * close button, another overlay taking over) releases it.
     * @param {boolean} on
     */
    lockScroll(on) {
      document.documentElement.classList.toggle('aaiko-cart-locked', !!on);
    }

    /**
     * Only one AAIKO overlay may own the screen at a time. Auto-open on
     * add-to-cart can fire while another overlay (the search panel) is open,
     * and that one paints above this panel — so focus would be trapped in a
     * dialog the user cannot see.
     *
     * Generic on purpose, in two steps, so no component's internals are known
     * here and future overlays are covered for free:
     *   1. call `close()` if the element exposes one (search panel, filters);
     *   2. otherwise click its own `data-aaiko-*-close` control, the theme-wide
     *      convention every AAIKO overlay already ships (the PDP details
     *      drawer has that hook but no `close()` method, and it paints at
     *      z-index 1000 — well above this panel — so skipping it would leave
     *      focus trapped under a visible modal).
     * Anything that offers neither is left alone; it must never block the bag.
     */
    dismissOtherOverlays() {
      var i;
      var nodes = document.querySelectorAll('*');

      // 1. Preferred: the element's own API.
      for (i = 0; i < nodes.length; i++) {
        var el = nodes[i];
        if (el === this || el.tagName.indexOf('AAIKO-') !== 0) continue;
        if (el.hidden || el.hasAttribute('hidden')) continue;
        if (typeof el.close !== 'function') continue;
        try {
          el.close();
        } catch (error) {
          /* an overlay refusing to close must never block the bag */
        }
      }

      // 2. Fallback for overlays with no `close()`: click their own dismiss
      //    control. Scanned across the whole document, not within the
      //    `aaiko-*` element, because a panel can be portalled to <body> — the
      //    PDP details drawer is, so it sits inside no custom element at all.
      //    Self-limiting: anything step 1 already closed is no longer visible.
      for (i = 0; i < nodes.length; i++) {
        var hook = /** @type {HTMLElement} */ (nodes[i]);
        if (this.contains(hook) || !document.contains(hook)) continue;
        if (!this.hasCloseHookAttribute(hook) || !this.isVisible(hook)) continue;
        try {
          hook.click();
        } catch (error) {
          /* same rule: never block the bag */
        }
      }
    }

    /**
     * Does this element carry a `data-aaiko-…-close` attribute? That is the
     * theme-wide convention for an overlay's public dismiss control (search,
     * filters, details, this drawer), and the only handle a portalled panel
     * offers from the outside.
     * @param {Element} el
     * @returns {boolean}
     */
    hasCloseHookAttribute(el) {
      var attrs = el.attributes;
      for (var i = 0; i < attrs.length; i++) {
        var name = attrs[i].name;
        if (name.indexOf('data-aaiko-') === 0 && name.slice(-6) === '-close') return true;
      }
      return false;
    }

    /**
     * Is the element actually rendered and on screen?
     * @param {Element} el
     * @returns {boolean}
     */
    isVisible(el) {
      if (typeof el.checkVisibility === 'function') {
        return el.checkVisibility({ checkOpacity: true, checkVisibilityCSS: true });
      }
      return el.getClientRects().length > 0;
    }


    onOpenEvent() {
      this.open();
    }

    /* ------------------------------------------------------------- events */

    onDocumentClick(event) {
      var trigger = event.target.closest ? event.target.closest('[data-aaiko-cart-open]') : null;
      if (trigger) {
        event.preventDefault();
        this.toggle();
        return;
      }
      // Progressive enhancement: a plain link to /cart opens the panel.
      var link = event.target.closest ? event.target.closest('a[href]') : null;
      if (!link || this.contains(link)) return;
      if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      if (link.hasAttribute('data-aaiko-cart-noopen') || link.target === '_blank') return;
      if (!isCartUrl(link.getAttribute('href'))) return;
      event.preventDefault();
      this.open();
    }

    onClick(event) {
      var target = event.target;
      if (target.closest('[data-aaiko-cart-close]') || target.closest('[data-aaiko-cart-scrim]')) {
        event.preventDefault();
        this.close();
        return;
      }
      var remove = target.closest('[data-aaiko-cart-remove]');
      if (remove) {
        event.preventDefault();
        this.change(Number(remove.dataset.line), 0);
      }
    }

    onChange(event) {
      var select = event.target.closest ? event.target.closest('[data-aaiko-cart-qty]') : null;
      if (!select) return;
      this.change(Number(select.dataset.line), Number(select.value));
    }

    /**
     * Checkout is a real form POST, so the page is about to navigate. Mark the
     * button busy for that gap and swallow any second submit. The button is
     * never `disabled` — that would drop its name/value from the POST.
     */
    onSubmit(event) {
      var form = event.target.closest ? event.target.closest('.aaiko-cart__footer') : null;
      if (!form) return;
      if (this.checkingOut) {
        event.preventDefault();
        return;
      }
      this.checkingOut = true;
      var button = form.querySelector('[data-aaiko-cart-checkout]');
      if (button) button.setAttribute('aria-busy', 'true');
      // If the navigation never happens (offline, blocked), give the control back.
      window.setTimeout(
        function () {
          this.checkingOut = false;
          if (button) button.removeAttribute('aria-busy');
        }.bind(this),
        8000
      );
    }

    onKeydown(event) {
      if (!this.isOpen) return;
      if (event.key === 'Escape') {
        // A size strip open on a recommendation tile takes this Escape:
        // aaiko-quickshop.js closes it on its own document listener, and
        // the panel stays. The next Escape closes the panel.
        if (this.querySelector('[data-quickshop-open]')) return;
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      var panel = this.panel;
      if (!panel) return;
      var nodes = Array.prototype.filter.call(panel.querySelectorAll(FOCUSABLE), function (el) {
        return el.offsetParent !== null || el === document.activeElement;
      });
      if (!nodes.length) return;
      var first = nodes[0];
      var last = nodes[nodes.length - 1];
      if (document.activeElement === panel) {
        // Focus starts on the dialog itself; Tab enters, Shift+Tab wraps back.
        if (event.shiftKey) {
          event.preventDefault();
          last.focus();
        }
        return;
      }
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (!panel.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      }
    }

    onCartUpdate(event) {
      // Ignore the echo of our own mutation.
      if (event && event.detail && event.detail.source === 'aaiko-cart-drawer') return;
      var shouldOpen = !event || !event.action || event.action === 'add';
      var settle = function () {
        return this.refresh().then(
          function () {
            if (shouldOpen && Number(this.dataset.itemCount) > 0) this.open();
          }.bind(this)
        );
      }.bind(this);

      if (event && event.promise && typeof event.promise.then === 'function') {
        event.promise.then(settle).catch(settle);
      } else {
        settle();
      }
    }

    /* ------------------------------------------------------------ mutation */

    change(line, quantity) {
      if (this.busy || !line) return Promise.resolve();
      this.busy = true;
      this.setAttribute('aria-busy', 'true');
      var body = { line: line, quantity: quantity, sections: this.sectionId, sections_url: this.sectionsUrl() };
      return fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart/change.js' : '/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      })
        .then(function (response) {
          return response.json();
        })
        .then(
          function (data) {
            if (data && data.sections && data.sections[this.sectionId]) {
              this.render(data.sections[this.sectionId]);
            }
            this.announce(data);
          }.bind(this)
        )
        .catch(function (error) {
          console.warn('[aaiko-cart-drawer] change failed', error);
        })
        .then(
          function () {
            this.busy = false;
            this.removeAttribute('aria-busy');
          }.bind(this)
        );
    }

    sectionsUrl() {
      return window.location.pathname + window.location.search;
    }

    refresh() {
      var url = new URL(window.location.href);
      url.searchParams.set('sections', this.sectionId);
      // No `Accept: application/json` here on purpose: /cart answers that
      // header with the cart JSON instead of the Section Rendering payload.
      return fetch(url.toString())
        .then(function (response) {
          return response.json();
        })
        .then(
          function (data) {
            if (data && data[this.sectionId]) this.render(data[this.sectionId]);
          }.bind(this)
        )
        .catch(function (error) {
          console.warn('[aaiko-cart-drawer] refresh failed', error);
        });
    }

    render(html) {
      var parsed = new DOMParser().parseFromString(html, 'text/html');
      var next = parsed.querySelector('[data-aaiko-cart-body]');
      var current = this.querySelector('[data-aaiko-cart-body]');
      if (!next || !current) return;
      var hadFocus = this.contains(document.activeElement);
      // Fade the incoming list and summary in rather than letting them snap.
      // The class comes off synchronously after a forced reflow, so the
      // regions can never be stranded invisible if transitions are disabled.
      next.classList.add('aaiko-cart__body--swap');
      current.replaceWith(next);
      void next.offsetHeight;
      next.classList.remove('aaiko-cart__body--swap');
      var host = parsed.querySelector('aaiko-cart-drawer');
      if (host && host.dataset.itemCount != null) this.dataset.itemCount = host.dataset.itemCount;
      this.syncCount();
      if (hadFocus && this.isOpen) {
        var panel = this.panel;
        if (panel) panel.focus();
      }
      // The fresh markup carries skeletons again; fill them.
      this.loadRecs();
    }

    /* ----------------------------------------------------- recommendations */

    /**
     * Fills "You may also like" from the recommendations engine. The section
     * puts the seed product and the fragment URL on the block; without them
     * (empty bag) the server-rendered fallback tiles stay as they are.
     *
     * Order of preference for what ends up in the grid:
     *   1. the engine's tiles, from sections/aaiko-cart-recs.liquid
     *   2. the fallback collection tiles the section stashed in a <template>
     *   3. nothing: the whole block is hidden
     */
    loadRecs() {
      var block = this.querySelector('[data-aaiko-cart-recs]');
      if (!block) return Promise.resolve();
      var seed = block.dataset.productId;
      var url = block.dataset.url;
      if (!seed || !url) return Promise.resolve();

      if (this.recsCache && this.recsCache.seed === seed) {
        this.applyRecs(block, this.recsCache.tiles);
        return Promise.resolve();
      }

      var request = (this.recsRequest = {});
      return fetch(url)
        .then(function (response) {
          return response.ok ? response.text() : Promise.reject(response.status);
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fragment = doc.querySelector('[data-aaiko-cart-recs-fragment]');
          return fragment ? fragment.innerHTML.trim() : '';
        })
        .catch(function (error) {
          console.warn('[aaiko-cart-drawer] recommendations failed', error);
          return '';
        })
        .then(
          function (tiles) {
            this.recsCache = { seed: seed, tiles: tiles };
            // A newer seed (another line added meanwhile) supersedes this one.
            if (request !== this.recsRequest) return;
            // The block may have been re-rendered since the request went out.
            var live = this.querySelector('[data-aaiko-cart-recs]');
            if (!live || live.dataset.productId !== seed) return;
            this.applyRecs(live, tiles);
          }.bind(this)
        );
    }

    /**
     * @param {HTMLElement} block the [data-aaiko-cart-recs] element
     * @param {string} tiles the engine's tile markup, possibly empty
     */
    applyRecs(block, tiles) {
      var grid = block.querySelector('[data-aaiko-cart-recs-grid]');
      if (!grid) return;
      if (!tiles) {
        var fallback = block.querySelector('[data-aaiko-cart-recs-fallback]');
        tiles = fallback ? fallback.innerHTML.trim() : '';
      }
      if (!tiles) {
        block.hidden = true;
        return;
      }
      block.hidden = false;
      grid.innerHTML = tiles;
    }

    /**
     * Keeps the header bag counter in step without owning the header markup.
     * The header only has to put `data-aaiko-cart-count` on the element that
     * shows the number; it is hidden while the count is 0.
     */
    syncCount() {
      var count = Number(this.dataset.itemCount) || 0;
      var nodes = document.querySelectorAll('[data-aaiko-cart-count]');
      for (var i = 0; i < nodes.length; i++) {
        nodes[i].textContent = String(count);
        nodes[i].toggleAttribute('hidden', count === 0);
      }
    }

    /* Let Horizon's cart icon and anything else on the standard event know. */
    announce(data) {
      var count = data && typeof data.item_count === 'number' ? data.item_count : Number(this.dataset.itemCount) || 0;
      var event = new CustomEvent(CART_EVENT, {
        bubbles: true,
        detail: { source: 'aaiko-cart-drawer', itemCount: count },
      });
      event.action = 'update';
      event.promise = Promise.resolve({ cart: { totalQuantity: count }, detail: { itemCount: count } });
      document.dispatchEvent(event);
    }
  }

  if (!customElements.get('aaiko-cart-drawer')) {
    customElements.define('aaiko-cart-drawer', AaikoCartDrawerComponent);
  }
})();
